import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaArrowLeft as FaArrowLeftRaw,
  FaBook as FaBookRaw,
  FaCalendarAlt as FaCalendarAltRaw,
  FaCheckCircle as FaCheckCircleRaw,
  FaClipboardList as FaClipboardListRaw,
  FaChevronDown as FaChevronDownRaw,
  FaChevronRight as FaChevronRightRaw,
  FaEdit as FaEditRaw,
  FaFilter as FaFilterRaw,
  FaPlus as FaPlusRaw,
  FaSearch as FaSearchRaw,
  FaUserFriends as FaUserFriendsRaw,
} from 'react-icons/fa';
import { teacherService, type TeacherLesson } from '../../services/classroom/teacherService';
import {
  LessonBlockRenderer,
  lessonBlockAccent,
  lessonBlockIcon,
  parseLessonStoredDescription,
  type LessonContentBlockType,
} from '../../components/classroom/LessonBlockRenderer';
import ClassSectionSidebar from '../../components/classroom/ClassSectionSidebar';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaArrowLeft: any = FaArrowLeftRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBook: any = FaBookRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCalendarAlt: any = FaCalendarAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCheckCircle: any = FaCheckCircleRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClipboardList: any = FaClipboardListRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronDown: any = FaChevronDownRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronRight: any = FaChevronRightRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEdit: any = FaEditRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFilter: any = FaFilterRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUserFriends: any = FaUserFriendsRaw;

const MOBILE_BREAKPOINT = '1024px';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const BackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

/** Matches class detail hero sizing (padding, title scale, border radius). */
const Hero = styled.section<{ $imageUrl?: string | null }>`
  border-radius: 16px;
  border: 1px solid var(--outline);
  padding: 1.5rem 1.65rem 1.35rem;
  color: var(--onPrimary);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.1);
  background: ${({ $imageUrl }) => {
    const base = 'linear-gradient(135deg, var(--primaryDark) 0%, var(--primary) 55%, color-mix(in srgb, var(--primary) 70%, var(--surface)) 100%)';
    const url = typeof $imageUrl === 'string' ? $imageUrl.trim() : '';
    if (!url) return base;
    return `linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.15) 100%), url(${url}) center / cover no-repeat, ${base}`;
  }};
`;

const Breadcrumb = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.88;
`;

/** Matches class detail `Title` spacing. */
const HeroTitle = styled.h1`
  margin: 0 0 1rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.45rem, 3vw, 2rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
`;

const HeroStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const HeroPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(6px);
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const SideNavFooter = styled.div`
  margin-top: 0.65rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--outline);
`;

const AddLessonBtn = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  padding: 0.65rem 0.75rem;
  border-radius: 12px;
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.82rem;
  font-weight: 700;
  text-decoration: none;
  &:hover {
    background: var(--primaryDark);
  }
`;

const MainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ $variant: 'primary' | 'muted' | 'warm' }>`
  border: 1px solid var(--outline);
  border-radius: 14px;
  background: var(--surface);
  padding: 0.95rem 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  border-left: 4px solid
    ${({ $variant }) =>
      $variant === 'primary'
        ? 'var(--primary)'
        : $variant === 'warm'
          ? 'var(--warning, #e8a23c)'
          : 'color-mix(in srgb, var(--primary) 45%, var(--outline))'};
`;

const StatTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const StatIcon = styled.span<{ $t: 'book' | 'check' | 'cal' }>`
  display: inline-flex;
  color: ${({ $t }) =>
    $t === 'book' ? 'var(--primary)' : $t === 'check' ? 'color-mix(in srgb, var(--primary) 75%, var(--text))' : 'var(--warning, #d97706)'};
  opacity: 0.9;
`;

const StatValue = styled.div`
  margin-top: 0.35rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.65rem;
  font-weight: 700;
  color: var(--text);
`;

const ModulesHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
`;

const ModulesTitle = styled.h2`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex: 1;
  min-width: 160px;
  max-width: 280px;
  padding: 0.45rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 0.85rem;
  font-family: inherit;
  color: var(--text);
  outline: none;
  &::placeholder {
    color: var(--textSecondary, var(--onSurfaceVariant));
  }
`;

const ToolBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const OutlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.85rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
  text-decoration: none;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.48rem 0.9rem;
  border-radius: 10px;
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.82rem;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    background: var(--primaryDark);
  }
`;

const ModuleBlock = styled.section`
  margin-top: 0.25rem;
`;

const ModuleHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
`;

const ModuleTitle = styled.h3`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--text);
`;

const ModuleBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const LessonCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 0.85rem 1rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const LessonTop = styled.div`
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) auto;
  gap: 0.85rem 1rem;
  align-items: start;
  @media (max-width: 640px) {
    grid-template-columns: 48px 1fr;
    grid-template-rows: auto auto;
  }
`;

const Thumb = styled.div<{ $accent: 'blue' | 'orange' | 'purple' | 'gray' }>`
  width: 52px;
  height: 52px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $accent }) =>
    $accent === 'blue'
      ? 'color-mix(in srgb, #2c7be5 14%, transparent)'
      : $accent === 'orange'
        ? 'color-mix(in srgb, #ea580c 14%, transparent)'
        : $accent === 'purple'
          ? 'color-mix(in srgb, #7c3aed 14%, transparent)'
          : 'var(--surfaceVariant)'};
  border: 1px solid var(--outline);
  color: ${({ $accent }) =>
    $accent === 'blue' ? '#2c7be5' : $accent === 'orange' ? '#ea580c' : $accent === 'purple' ? '#7c3aed' : 'var(--textSecondary)'};
  @media (max-width: 640px) {
    width: 48px;
    height: 48px;
  }
`;

const LessonBody = styled.div`
  min-width: 0;
`;

const TypeLine = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const LessonTitle = styled.div`
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--text);
`;

const StatusBadge = styled.span<{ $status: 'published' | 'scheduled' | 'draft' }>`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
  justify-self: end;
  align-self: start;
  ${({ $status }) =>
    $status === 'published'
      ? `
    background: var(--successContainer, rgba(34, 160, 107, 0.15));
    color: var(--success, #1a8f5c);
  `
      : $status === 'scheduled'
        ? `
    background: var(--primaryContainer, var(--surfaceVariant));
    color: var(--primary);
  `
        : `
    background: color-mix(in srgb, var(--warning, #e8a23c) 22%, transparent);
    color: var(--warning, #92400e);
  `}
  @media (max-width: 640px) {
    grid-column: 2 / -1;
    justify-self: start;
  }
`;

const LessonTopActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  justify-self: end;
`;

const EditLessonLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.28rem 0.56rem;
  text-decoration: none;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const ExpandToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.28rem 0.52rem;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const LessonSummary = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.82rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
`;

const Empty = styled.p`
  margin: 0;
  padding: 1.25rem;
  border-radius: 14px;
  border: 1px dashed var(--outline);
  background: var(--surfaceVariant);
  font-size: 0.9rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

function lessonStatus(lesson: TeacherLesson): 'published' | 'scheduled' | 'draft' {
  if (!lesson.lesson_date) return 'draft';
  const date = new Date(lesson.lesson_date);
  if (Number.isNaN(date.getTime())) return 'draft';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day > today ? 'scheduled' : 'published';
}

function classCodeLabel(id: string, className: string): string {
  const short = id.replace(/-/g, '').slice(0, 6).toUpperCase();
  const topic = className.split(/\s+/).slice(0, 2).join(' ').toUpperCase();
  return `${topic || 'CLASS'} · ${short || 'NEW'}`;
}

const ClassroomTeacherClassLessonsPage: React.FC = () => {
  const { classId = '' } = useParams<{ classId: string }>();
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedLessonIds, setExpandedLessonIds] = useState<Set<string>>(new Set());

  const [overviewQuery, lessonsQuery, assignmentsQuery, quizzesQuery] = useQueries({
    queries: [
      { queryKey: ['classroom', 'teacher', 'overview'], queryFn: teacherService.getOverview },
      { queryKey: ['classroom', 'teacher', 'lessons'], queryFn: teacherService.getLessons },
      { queryKey: ['classroom', 'teacher', 'work', 'assignment'], queryFn: () => teacherService.getWork('assignment') },
      { queryKey: ['classroom', 'teacher', 'work', 'quiz'], queryFn: () => teacherService.getWork('quiz') },
    ],
  });

  const lessonsEditorHref = `/classroom-teacher/classes/${encodeURIComponent(classId)}/lessons/new`;

  if (overviewQuery.isLoading || lessonsQuery.isLoading || assignmentsQuery.isLoading || quizzesQuery.isLoading) {
    return <Empty>Loading class lessons…</Empty>;
  }

  if (overviewQuery.isError || lessonsQuery.isError || assignmentsQuery.isError || quizzesQuery.isError) {
    return <Empty>Unable to load class lessons.</Empty>;
  }

  const classItem = (overviewQuery.data ?? []).find((row) => row.id === classId);
  if (!classItem) {
    return (
      <Page>
        <BackRow>
          <BackLink to="/classroom-teacher/classes">
            <FaArrowLeft size={12} aria-hidden />
            Back to Classes
          </BackLink>
        </BackRow>
        <Empty>Class not found.</Empty>
      </Page>
    );
  }

  const lessonsAll = (lessonsQuery.data ?? [])
    .filter((lesson) => lesson.class_id === classId)
    .sort((a, b) => a.sequence - b.sequence || a.title.localeCompare(b.title));

  const q = search.trim().toLowerCase();
  const lessons = q
    ? lessonsAll.filter(
        (l) =>
          l.title.toLowerCase().includes(q) ||
          (l.description ?? '').toLowerCase().includes(q)
      )
    : lessonsAll;

  const toggleExpanded = (lessonId: string) => {
    setExpandedLessonIds((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) next.delete(lessonId);
      else next.add(lessonId);
      return next;
    });
  };

  const stats = {
    total: lessonsAll.length,
    published: lessonsAll.filter((l) => lessonStatus(l) === 'published').length,
    upcoming: lessonsAll.filter((l) => {
      const s = lessonStatus(l);
      return s === 'scheduled' || s === 'draft';
    }).length,
  };

  const shortClassName = classItem.name.split(/\s+/).slice(0, 3).join(' ') || 'This class';
  const classAssignments = (assignmentsQuery.data ?? []).filter((row) => row.class_id === classId);
  const classQuizzes = (quizzesQuery.data ?? []).filter((row) => row.class_id === classId);
  const assignmentCount = classAssignments.length + classQuizzes.length;
  const classLessonsCount = (lessonsQuery.data ?? []).filter((l) => l.class_id === classId).length;

  return (
    <Page>
      <BackRow>
        <BackLink to="/classroom-teacher/classes">
          <FaArrowLeft size={12} aria-hidden />
          Back to Classes
        </BackLink>
      </BackRow>

      <Hero $imageUrl={classItem.class_image_url}>
        <Breadcrumb>{classCodeLabel(classId, classItem.name)}</Breadcrumb>
        <HeroTitle>Lessons management</HeroTitle>
        
        <HeroStats>
          <HeroPill>
            <FaUserFriends size={14} aria-hidden />
            {classItem.activeStudents} Students
          </HeroPill>
          <HeroPill>
            <FaBook size={14} aria-hidden />
            {classLessonsCount} Lessons
          </HeroPill>
          <HeroPill>
            <FaClipboardList size={14} aria-hidden />
            {assignmentCount} Assignments
          </HeroPill>
        </HeroStats>
      </Hero>

      <LayoutGrid>
        <ClassSectionSidebar classId={classId} className={shortClassName}>
          <SideNavFooter>
            <AddLessonBtn to={lessonsEditorHref}>
              <FaPlus size={13} aria-hidden />
              Add new lesson
            </AddLessonBtn>
          </SideNavFooter>
        </ClassSectionSidebar>

        <MainCol>
          <StatRow>
            <StatCard $variant="primary">
              <StatTop>
                <StatLabel>TOTAL LESSONS</StatLabel>
                <StatIcon $t="book" aria-hidden>
                  <FaBook size={16} />
                </StatIcon>
              </StatTop>
              <StatValue>{stats.total}</StatValue>
            </StatCard>
            <StatCard $variant="muted">
              <StatTop>
                <StatLabel>PUBLISHED</StatLabel>
                <StatIcon $t="check" aria-hidden>
                  <FaCheckCircle size={16} />
                </StatIcon>
              </StatTop>
              <StatValue>{stats.published}</StatValue>
            </StatCard>
            <StatCard $variant="warm">
              <StatTop>
                <StatLabel>UPCOMING</StatLabel>
                <StatIcon $t="cal" aria-hidden>
                  <FaCalendarAlt size={15} />
                </StatIcon>
              </StatTop>
              <StatValue>{stats.upcoming}</StatValue>
            </StatCard>
          </StatRow>

          <div>
            <ModulesHead>
              <ModulesTitle>Lesson modules</ModulesTitle>
              <Toolbar>
                <SearchWrap>
                  <FaSearch size={14} aria-hidden />
                  <SearchInput
                    placeholder="Search lessons…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search lessons"
                  />
                </SearchWrap>
                <ToolBtn type="button" onClick={() => setFilterOpen((o) => !o)} aria-expanded={filterOpen}>
                  <FaFilter size={13} aria-hidden />
                  Filter
                </ToolBtn>
                <OutlineLink to={lessonsEditorHref}>Create module</OutlineLink>
                <PrimaryLink to={lessonsEditorHref}>
                  <FaPlus size={12} aria-hidden />
                  New lesson
                </PrimaryLink>
              </Toolbar>
            </ModulesHead>
            {filterOpen ? (
              <Empty style={{ marginTop: '0.5rem', padding: '0.65rem 0.85rem', fontSize: '0.8rem' }}>
                Filters coming soon — use search for now.
              </Empty>
            ) : null}
          </div>

          <ModuleBlock>
            <ModuleHead>
              <ModuleTitle>Module 1: {classItem.name}</ModuleTitle>
              <ModuleBadge>
                {lessonsAll.length} {lessonsAll.length === 1 ? 'LESSON' : 'LESSONS'}
              </ModuleBadge>
            </ModuleHead>
            {lessons.length === 0 ? (
              <Empty>
                {lessonsAll.length === 0
                  ? 'No lessons yet. Use “Add new lesson” to build this module.'
                  : 'No lessons match your search.'}
              </Empty>
            ) : (
              lessons.map((lesson) => {
                const status = lessonStatus(lesson);
                const parsed = parseLessonStoredDescription(lesson.description);
                const lessonBlocks = parsed.blocks;
                const topBlock = lessonBlocks[0];
                const thumbType: LessonContentBlockType = topBlock?.type ?? 'text';
                const ThumbIcon = lessonBlockIcon(thumbType);
                const thumbAccent = lessonBlockAccent(thumbType);
                const isExpanded = expandedLessonIds.has(lesson.id);
                const lessonEditHref = `/classroom-teacher/classes/${encodeURIComponent(classId)}/lessons/${encodeURIComponent(lesson.id)}/edit`;
                return (
                  <LessonCard key={lesson.id}>
                    <LessonTop>
                      <Thumb $accent={thumbAccent} aria-hidden>
                        <ThumbIcon size={16} />
                      </Thumb>
                      <LessonBody>
                        <TypeLine>
                          {lessonBlocks.length} {lessonBlocks.length === 1 ? 'BLOCK' : 'BLOCKS'}
                        </TypeLine>
                        <LessonTitle>{lesson.title}</LessonTitle>
                        {parsed.plainDescription ? <LessonSummary>{parsed.plainDescription}</LessonSummary> : null}
                      </LessonBody>
                      <LessonTopActions>
                        <EditLessonLink to={lessonEditHref}>
                          <FaEdit size={10} aria-hidden />
                          Edit
                        </EditLessonLink>
                        <ExpandToggle
                          type="button"
                          aria-expanded={isExpanded}
                          onClick={() => toggleExpanded(lesson.id)}
                        >
                          {isExpanded ? <FaChevronDown size={10} aria-hidden /> : <FaChevronRight size={10} aria-hidden />}
                          {isExpanded ? 'Hide blocks' : 'Show blocks'}
                        </ExpandToggle>
                        <StatusBadge $status={status}>{status.toUpperCase()}</StatusBadge>
                      </LessonTopActions>
                    </LessonTop>

                    {isExpanded ? (
                      lessonBlocks.length > 0 ? (
                      <LessonBlockRenderer
                        blocks={lessonBlocks}
                        quizzes={classQuizzes}
                        assignments={classAssignments}
                        classId={classId}
                        showQuizAnswers
                      />
                      ) : (
                      <LessonSummary style={{ marginTop: 0 }}>
                        No content blocks yet. Open this lesson in the editor to build lesson content.
                      </LessonSummary>
                      )
                    ) : null}
                  </LessonCard>
                );
              })
            )}
          </ModuleBlock>
        </MainCol>
      </LayoutGrid>
    </Page>
  );
};

export default ClassroomTeacherClassLessonsPage;
